import Button from "@/components/base/Button";
import { orthopaedicServices, spineHighlights, spineProcedures } from "@/mocks/site";

export function OrthoFeature() {
  const bubbleColors = [
    "bg-primary-100 text-primary-600",
    "bg-secondary-100 text-secondary-600",
    "bg-accent-100 text-accent-600",
    "bg-secondary-100 text-secondary-700",
  ];

  return (
    <section id="orthopaedic-care" className="scroll-mt-28 bg-gradient-to-b from-accent-50/70 via-background-50 to-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="reveal relative lg:col-span-6">
            <div
              className="absolute -left-5 -top-5 bottom-16 w-2/3 rounded-2xl bg-gradient-to-br from-primary-100 to-accent-100"
              aria-hidden="true"
            ></div>
            <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-100">
              <div className="h-[380px] w-full md:h-[520px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Photorealistic%20still%20life%20of%20a%20knee%20joint%20model%20with%20orthopaedic%20instruments%20on%20a%20clean%20white%20consultation%20desk%20in%20a%20bright%20modern%20clinic%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=1000&height=800&seq=ospc-feature-ortho-2026&orientation=landscape"
                  alt="Knee joint model used to explain orthopaedic treatment in Trichy"
                  title="Orthopaedic care in Trichy"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="absolute -bottom-5 right-4 flex items-center gap-3 rounded-2xl border border-background-200 bg-background-50/95 px-5 py-4 shadow-[0_20px_45px_-30px_rgba(15,40,80,0.5)] backdrop-blur md:right-8">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-focus-3-line text-xl" aria-hidden="true"></i>
              </span>
              <span>
                <span className="block font-heading text-sm font-semibold text-foreground-950">
                  Joint replacement &amp; arthroscopy
                </span>
                <span className="block font-label text-xs text-foreground-600">Minimally invasive where suitable</span>
              </span>
            </div>
          </div>

          <div className="reveal lg:col-span-6">
            <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.18em] text-accent-700">
              <span className="h-px w-8 bg-gradient-to-r from-primary-400 to-accent-400" aria-hidden="true"></span>
              Orthopaedic Care
            </span>
            <h2 className="mt-5 font-heading text-3xl font-semibold leading-[1.14] text-foreground-950 md:text-4xl">
              Expert care for bones, joints, muscles and ligaments
            </h2>
            <p className="mt-5 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              Orthopaedic conditions can affect movement, confidence and quality of life. We treat the full range — from
              arthritis and sports injuries to complex fractures — with a plan tailored to your needs.
            </p>

            <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
              {orthopaedicServices.map((item, index) => (
                <div key={item.title} className="flex items-start gap-3">
                  <span
                    className={`flex h-9 w-9 flex-none items-center justify-center rounded-md ${
                      bubbleColors[index % bubbleColors.length]
                    }`}
                  >
                    <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-0.5 font-body text-xs leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-9">
              <Button to="/services/orthopaedic-care" size="lg" icon="ri-arrow-right-line">
                Explore Orthopaedic Care
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SpineFeature() {
  return (
    <section id="spine-care" className="scroll-mt-28 bg-gradient-to-b from-secondary-50 via-background-50 to-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="reveal order-2 lg:order-1 lg:col-span-6">
            <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.18em] text-secondary-700">
              <span className="h-px w-8 bg-gradient-to-r from-secondary-400 to-primary-400" aria-hidden="true"></span>
              Spine Care
            </span>
            <h2 className="mt-5 font-heading text-3xl font-semibold leading-[1.14] text-foreground-950 md:text-4xl">
              Dedicated spine care, from diagnosis to recovery
            </h2>
            <p className="mt-5 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              Persistent neck and back pain deserves a specialist opinion. Our spine care covers the full pathway —
              accurate diagnosis, non-surgical treatment where possible, and advanced surgery when it is needed.
            </p>

            <div className="mt-8 space-y-4">
              {spineHighlights.map((item) => (
                <div key={item.title} className="flex gap-4 rounded-xl border border-background-200 bg-background-50 p-4">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-1 font-body text-xs leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-9">
              <Button to="/services/spine-care" variant="accent" size="lg" icon="ri-arrow-right-line">
                Explore Spine Care
              </Button>
            </div>
          </div>

          <div className="reveal order-1 lg:order-2 lg:col-span-6">
            <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <div className="h-[340px] w-full md:h-[440px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Photorealistic%20still%20life%20of%20an%20anatomical%20spine%20model%20and%20an%20MRI%20scan%20film%20on%20a%20clean%20desk%20in%20a%20bright%20consultation%20room%2C%20soft%20cyan%20and%20teal%20lighting%2C%20clean%20minimal%20background%2C%20premium%20medical%20editorial%20photography%2C%20no%20people&width=1000&height=800&seq=ospc-feature-spine-2026&orientation=landscape"
                  alt="Anatomical spine model and MRI scan used to explain spine care in Trichy"
                  title="Spine specialist in Trichy"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="mt-5 rounded-2xl border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-base font-semibold text-foreground-950">Spine conditions we treat</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {spineProcedures.map((item) => (
                  <span
                    key={item}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 font-label text-xs font-medium text-secondary-900"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}